/**
 * ProjectWelcome — the workspace chat panel's empty state (replaced the
 * persona picker, 2026-07-15). Its job is to make the agent's visibility
 * contract legible before the first message: this project's folder plus
 * the linked folders below, nothing else. Rows mirror the explorer's
 * language (Link2 glyph, Lock = read-only, permission labels) — and its
 * WORDING, deliberately: the two rows are the explorer's two sections, so
 * "In this Project" / "Linked from Drives" have to travel together. They
 * did not before `entityKind` existed here: this row said "Project files"
 * in a PDApp workspace, where the explorer two inches away said "PDApp".
 *
 * `linkedFolders` is display-ready data resolved by the HOST — the
 * workspace wrapper owns workspace config, and this package deliberately
 * doesn't depend on it. undefined = the host didn't wire links; the card
 * then shows only the project-files row rather than claiming "none".
 *
 * Responsive by CONTAINER, not viewport: the chat panel is drag-resizable
 * (and full-width on mobile), so everything is fluid — w-full up to a cap,
 * truncating rows/chips, m-auto so short panels scroll instead of clip.
 */
import { LayoutGrid, Link2, Lock } from 'lucide-react'

export interface ScopeLink {
  /** Display name — the link alias, else the target folder's name. */
  name: string
  permission: 'r' | 'rw'
  /** Target folder deleted / unresolvable — shown greyed, not hidden. */
  missing?: boolean
}

/** Kind-aware, because these say "project" out loud and sat unchanged in a
 *  PDApp workspace whose every other label said PDApp. */
const starters = (noun: string): Array<{ label: string; prompt: string }> => [
  { label: `Tour this ${noun}`, prompt: `Give me a quick tour of this ${noun} — what's here and what is each file for?` },
  { label: 'Summarize the files', prompt: `Read through this ${noun}'s files and summarize what each one contains.` },
]

export function ProjectWelcome({ linkedFolders, onStart, entityKind, projectCanWrite }: {
  linkedFolders?: ScopeLink[]
  onStart: (prompt: string) => void
  /** Container kind — mirrors the explorer's section name. Absent ⇒ project,
   *  which is what an unwired host and every non-app workspace already are. */
  entityKind?: 'project' | 'miniapp'
  /** Can the person write this project's own folder (the host's `canWriteFor`)? Only `true` says
   *  "Read & Write" — anything else (read-only, a hallway project opened on its config read (#1406),
   *  a host that didn't wire it) under-claims "Read-only", as the linked rows below do (#327). */
  projectCanWrite?: boolean
}) {
  const entityNoun = entityKind === 'miniapp' ? 'PDApp' : 'Project'
  return (
    <div className="flex-1 min-h-0 overflow-y-auto flex p-4">
      <div className="m-auto w-full max-w-[400px]">
        <p className="text-base themed-text text-center mb-1">What should we work on?</p>
        <p className="text-xs themed-text-muted text-center mb-4">Here&rsquo;s what I can see in this chat:</p>

        <div className="rounded-xl border themed-border themed-bg-secondary px-3.5 py-3 space-y-2">
          <div className="flex items-center gap-2 text-[12.5px]">
            <LayoutGrid size={13} className="chat-proj-icon shrink-0" aria-hidden />
            <span className="themed-text flex-1 min-w-0 truncate">In this {entityNoun}</span>
            {projectCanWrite === true ? (
              <span className="text-[11px] themed-text-muted shrink-0" data-testid="welcome-project-access">Read &amp; Write</span>
            ) : (
              <span className="flex items-center gap-1 text-[11px] themed-text-muted shrink-0" data-testid="welcome-project-access"><Lock size={10} aria-hidden />Read-only</span>
            )}
          </div>
          {(linkedFolders ?? []).map((l, i) => (
            <div key={i} className={`flex items-center gap-2 text-[12.5px] ${l.missing ? 'opacity-50' : ''}`}>
              <Link2 size={13} className="themed-text-muted shrink-0" aria-hidden />
              <span className="themed-text flex-1 min-w-0 truncate" data-testid="welcome-linked-folder">{l.name}</span>
              {/* (#327) Branch on the PERMISSIVE value, so anything unexpected —
                  undefined, a future value, a server that couldn't determine the
                  cap — under-claims instead of over-claiming. The inverse shape
                  ("=== 'r' ? read-only : read & write") sent every unknown to
                  "Read & Write", telling the user their AI could edit a folder it
                  cannot, then sending it into a write that fails. Under-claiming
                  costs someone not realising they can edit; over-claiming is the
                  bug a live check already caught once. */}
              {l.missing ? (
                <span className="text-[11px] themed-text-muted shrink-0">missing</span>
              ) : l.permission === 'rw' ? (
                <span className="text-[11px] themed-text-muted shrink-0">Read &amp; Write</span>
              ) : (
                <span className="flex items-center gap-1 text-[11px] themed-text-muted shrink-0"><Lock size={10} aria-hidden />Read-only</span>
              )}
            </div>
          ))}
          {linkedFolders && linkedFolders.length === 0 && (
            <p className="text-[11px] themed-text-muted leading-snug">
              Nothing linked from a drive yet — link a folder in the explorer to bring outside files into my view.
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-1.5 mt-3.5">
          {starters(entityNoun).map(s => (
            <button
              key={s.label}
              onClick={() => onStart(s.prompt)}
              className="px-3 py-1.5 rounded-full border themed-border themed-bg-secondary hover:themed-bg-tertiary text-xs themed-text-secondary hover:themed-text transition-colors max-w-full truncate"
            >
              {s.label}
            </button>
          ))}
        </div>

        <p className="text-[11px] themed-text-muted text-center mt-3 leading-snug">
          The file you have open is shared with each message — toggle that with the context chip in the composer.
        </p>
      </div>
    </div>
  )
}
